import { createFileRoute, useNavigate } from '@tanstack/solid-router';
import { For, Show, createMemo, createSignal, onMount } from 'solid-js';

import Header from '~/components/layout/Header';
import { type BuildingOption, listBuildings } from '~/lib/config/buildings';
import { firstSlug } from '~/lib/config/questions';
import { BUILDING_TYPE, COUNTRY_CODE } from '~/lib/graphql/types';
import { trackEvent, trackSelectedAddress } from '~/lib/helpers/analytics';
import { posthog } from '~/lib/helpers/posthog';
import {
    type SelectResult,
    usePlacesAutocomplete
} from '~/lib/hooks/usePlacesAutocomplete';
import { countryCode } from '~/lib/i18n';
import { useCalculator } from '~/lib/store';

export const Route = createFileRoute('/$locale/')({
    component: HomePage
});

function HomePage() {
    const [state, actions] = useCalculator();
    const navigate = useNavigate();

    const [selectedType, setSelectedType] = createSignal<BUILDING_TYPE | null>(
        null
    );
    const [selectedPlace, setSelectedPlace] = createSignal<SelectResult | null>(
        null
    );
    const [mapsReady, setMapsReady] = createSignal(false);

    onMount(() => posthog.capture('$pageview'));

    // Wait for Google Maps API to load
    onMount(() => {
        function check() {
            if (
                typeof google !== 'undefined' &&
                google.maps?.places?.AutocompleteSuggestion
            ) {
                setMapsReady(true);
            } else {
                setTimeout(check, 200);
            }
        }
        check();
    });

    const buildings = createMemo(() => listBuildings(countryCode()));

    // Places Autocomplete — only functional when Maps API is loaded
    const autocomplete = usePlacesAutocomplete(countryCode);
    const [showSuggestions, setShowSuggestions] = createSignal(false);

    function handleCountryToggle(code: COUNTRY_CODE) {
        setSelectedPlace(null);
        autocomplete.clear();

        const available = listBuildings(code);
        if (
            selectedType() &&
            !available.some((b) => b.value === selectedType())
        ) {
            setSelectedType(null);
        }

        const newLocale = code === COUNTRY_CODE.SG ? 'en-SG' : 'en-MY';
        navigate({
            to: '/$locale',
            params: { locale: newLocale },
            search: (prev) => prev
        });
    }

    async function handleSuggestionSelect(index: number) {
        const result = await autocomplete.onSelect(index);
        if (result) {
            setSelectedPlace(result);
            autocomplete.setInputValue(result.address);
            setShowSuggestions(false);
        }
    }

    function handleSubmit(e: SubmitEvent) {
        e.preventDefault();

        const place = selectedPlace();
        if (!place && !autocomplete.inputValue()) return;
        if (!selectedType()) return;

        actions.setQuote(
            'address',
            place?.address ?? autocomplete.inputValue()
        );
        actions.setQuote('lat', place?.lat ?? null);
        actions.setQuote('lng', place?.lng ?? null);
        actions.setQuote(
            'address_components',
            place?.addressComponents ?? ([] as never)
        );
        actions.setQuote('type', selectedType());

        actions.setSet(window.innerWidth >= 768 ? 'desktop' : 'mobile');
        actions.setStep(0);
        actions.setDirection('continue');
        trackSelectedAddress(selectedType());

        trackEvent('Step: address', {
            quote: state.quote,
            set: state.set,
            direction: 'continue',
            utm: state.utm
        });

        const slug = firstSlug(countryCode(), selectedType()!);
        navigate({
            to: '/$locale/question/$slug',
            params: { slug },
            search: (prev) => prev
        });
    }

    const bgImage = createMemo(() =>
        countryCode() === COUNTRY_CODE.MY
            ? '/landing_solar_my.jpg'
            : '/landing_solar_sg.jpg'
    );

    return (
        <div class="min-h-screen relative">
            {/* Background */}
            <div
                class="absolute inset-0 bg-cover bg-center"
                style={{
                    'background-image': `url(${bgImage()})`,
                    'background-color': '#1a365d'
                }}
            />
            <div class="absolute inset-0 bg-black/30" />

            {/* Header */}
            <div class="relative z-10">
                <Header />
            </div>

            {/* Main Content */}
            <div class="relative z-10 flex items-center justify-center min-h-[calc(100vh-80px)] p-4">
                <form
                    onSubmit={handleSubmit}
                    class="w-full max-w-[520px] bg-white rounded-2xl shadow-xl px-6 py-8 xs:px-8 sm:px-10 sm:py-10">
                    <h1 class="text-2xl sm:text-3xl font-heading font-bold text-brand-blue text-center leading-snug">
                        Calculate Your Solar Savings
                    </h1>
                    <p class="mt-2 text-sm text-gray-500 text-center">
                        Find out how much you can save by switching to solar.
                    </p>

                    {/* Country Toggle */}
                    <div class="mt-6">
                        <label class="block text-sm font-medium text-gray-700 mb-2">
                            Country
                        </label>
                        <div class="flex gap-2">
                            <For each={[COUNTRY_CODE.SG, COUNTRY_CODE.MY]}>
                                {(code) => (
                                    <button
                                        type="button"
                                        class={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg border-2 text-sm font-semibold transition-colors text-brand-blue ${
                                            countryCode() === code
                                                ? 'border-brand-blue bg-brand-blue/25'
                                                : 'border-brand-blue/25 bg-white hover:border-brand-blue/50'
                                        }`}
                                        onClick={() =>
                                            handleCountryToggle(code)
                                        }>
                                        <img
                                            src={
                                                code === COUNTRY_CODE.SG
                                                    ? '/singapore-flag.svg'
                                                    : '/malaysia-flag.svg'
                                            }
                                            alt=""
                                            width="20"
                                            height="14"
                                            class="inline-block shrink-0"
                                        />
                                        <span>
                                            {code === COUNTRY_CODE.SG
                                                ? 'Singapore'
                                                : 'Malaysia'}
                                        </span>
                                    </button>
                                )}
                            </For>
                        </div>
                    </div>

                    {/* Address Input with Autocomplete */}
                    <div class="mt-5 relative">
                        <label
                            class="block text-sm font-medium text-gray-700 mb-2"
                            for="address">
                            Your Address
                        </label>
                        <input
                            id="address"
                            type="text"
                            autocomplete="off"
                            placeholder={
                                mapsReady()
                                    ? 'Start typing your address...'
                                    : 'Loading address search...'
                            }
                            value={autocomplete.inputValue()}
                            onInput={(e) => {
                                autocomplete.setInputValue(
                                    e.currentTarget.value
                                );
                                setSelectedPlace(null);
                                setShowSuggestions(true);
                            }}
                            onFocus={() => {
                                if (autocomplete.suggestions().length > 0) {
                                    setShowSuggestions(true);
                                }
                            }}
                            onBlur={() => {
                                // Delay to allow click on suggestion
                                setTimeout(
                                    () => setShowSuggestions(false),
                                    200
                                );
                            }}
                            class="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent"
                        />

                        {/* Suggestions dropdown */}
                        <Show
                            when={
                                showSuggestions() &&
                                autocomplete.suggestions().length > 0
                            }>
                            <ul class="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                <For each={autocomplete.suggestions()}>
                                    {(suggestion, index) => (
                                        <li>
                                            <button
                                                type="button"
                                                class="w-full text-left px-4 py-3 text-sm hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                                                onMouseDown={(e) => {
                                                    e.preventDefault();
                                                    handleSuggestionSelect(
                                                        index()
                                                    );
                                                }}>
                                                {autocomplete.enrichedLabels()[
                                                    suggestion.placeId
                                                ] || suggestion.text}
                                            </button>
                                        </li>
                                    )}
                                </For>
                            </ul>
                        </Show>
                    </div>

                    {/* Building Type */}
                    <div class="mt-5">
                        <label class="block text-sm font-medium text-gray-700 mb-2">
                            Building Type
                        </label>
                        <div class="grid grid-cols-2 gap-3">
                            <For each={buildings()}>
                                {(building: BuildingOption) => (
                                    <button
                                        type="button"
                                        class={`min-w-0 p-2.5 xs:p-3 rounded-lg border-2 text-sm font-medium transition-colors text-left break-words min-h-[48px] flex items-center text-brand-blue ${
                                            selectedType() === building.value
                                                ? 'border-brand-blue bg-brand-blue/25'
                                                : 'border-brand-blue/25 bg-white hover:border-brand-blue/50'
                                        }`}
                                        onClick={() =>
                                            setSelectedType(building.value)
                                        }>
                                        {building.label.replaceAll('/', ' / ')}
                                    </button>
                                )}
                            </For>
                        </div>
                    </div>

                    {/* Submit */}
                    <button
                        type="submit"
                        disabled={
                            (!selectedPlace() && !autocomplete.inputValue()) ||
                            !selectedType()
                        }
                        class="mt-8 w-full py-3.5 rounded-full bg-brand-yellow text-brand-black font-semibold text-base transition-opacity disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90">
                        Calculate
                    </button>
                </form>
            </div>
        </div>
    );
}
